import * as React from 'react';

import {
  enumInventoryTagSortOption,
  enumInventoryTagStatus,
  enumTagType,
  InventoryTagSortOption,
} from '@rematter/frontend-lib-api-client';
import {
  DataGrid,
  Pagination,
  type PaginationProps,
  SearchField,
  Select,
  Button,
  ListScreenLayout,
  type ListStatsCardProps,
  DialogOverlay,
  DialogContent,
  DialogHeader,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  LucideTag,
  LucideCheckCircle,
  LucideFileText,
  LucidePackage,
  LucidePlus,
  LucideAlertTriangle,
} from 'lucide-react';

import { deleteInventoryTagAtom } from '../../_graphql/mutationInventoryTag';
import { $remoteFacilities } from '../../_state/facilities.state';
import * as state from '../../_state/inventoryTagsList.state';
import { useTranslations } from '../../i18n/useTranslations';
import { InventoryTagRecord } from '../../types/inventoryTag';

import { InventoryTagDialog } from './InventoryTagDialog';
import { useInventoryTagsColumns } from './useInventoryTagsColumns';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

const COLUMN_TO_SORT_MAP: Record<
  string,
  {
    asc: InventoryTagSortOption;
    desc: InventoryTagSortOption;
  }
> = {
  tagNumber: {
    asc: enumInventoryTagSortOption.TagNumberAsc,
    desc: enumInventoryTagSortOption.TagNumberDesc,
  },
  generatedAt: {
    asc: enumInventoryTagSortOption.GeneratedAtAsc,
    desc: enumInventoryTagSortOption.GeneratedAtDesc,
  },
};

export const InventoryTagsListView = () => {
  const t = useTranslations();
  const toast = useToast();
  const [, setLocation] = useModuleLocation();

  const [searchTerm, setSearchTerm] = useAtom(state.$searchTerm);
  const [selectedFacility, setSelectedFacility] = useAtom(
    state.$selectedFacility,
  );
  const [selectedTagType, setSelectedTagType] = useAtom(state.$selectedTagType);
  const [selectedStatus, setSelectedStatus] = useAtom(state.$selectedStatus);
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const setCurrentSorting = useSetAtom(state.$currentSorting);

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useAtom(
    state.$isCreateDialogOpen,
  );
  const [editingTag, setEditingTag] = React.useState<InventoryTagRecord | null>(
    null,
  );
  const [deletingTag, setDeletingTag] =
    React.useState<InventoryTagRecord | null>(null);

  const { data, isFetching, loadingState, error, refetch } = useAtomValue(
    state.$remoteInventoryTags,
  );

  const { data: facilitiesData } = useAtomValue($remoteFacilities);
  const facilities = React.useMemo(
    () => facilitiesData?.facilities ?? [],
    [facilitiesData?.facilities],
  );

  const facilityMap = React.useMemo(() => {
    const map = new Map<string, string>();
    facilities.forEach((f) => map.set(f.id, f.name));
    return map;
  }, [facilities]);

  const deleteTag = useSetAtom(deleteInventoryTagAtom({}));

  const tags = React.useMemo(
    () =>
      (data?.tags ?? []).map((tag) => ({
        ...tag,
        facilityName: facilityMap.get(tag.facilityId) || tag.facilityId,
      })),
    [data?.tags, facilityMap],
  );

  const stats = data?.stats ?? {
    totalTags: 0,
    activeTags: 0,
    draftTags: 0,
    inInventoryTags: 0,
  };
  const total = data?.totalCount ?? 0;

  const handlePaginationChange: PaginationProps['onChange'] = ({
    limit: newLimit,
    offset: newOffset,
  }) => {
    setLimit(newLimit);
    setOffset(newOffset);
  };

  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setCurrentSorting([enumInventoryTagSortOption.TagNumberAsc]);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            direction === 'ascending' ? sortMap.asc : sortMap.desc;
          setCurrentSorting([sortOption]);
        }
      }
      setOffset(0);
    },
    [setCurrentSorting, setOffset],
  );

  const handleCreateClick = React.useCallback(() => {
    setIsCreateDialogOpen(true);
  }, [setIsCreateDialogOpen]);

  const handleDialogClose = React.useCallback(() => {
    setIsCreateDialogOpen(false);
    setEditingTag(null);
  }, [setIsCreateDialogOpen]);

  const handleDialogSuccess = React.useCallback(() => {
    refetch();
    handleDialogClose();
  }, [refetch, handleDialogClose]);

  const handleEdit = React.useCallback((tag: InventoryTagRecord) => {
    setEditingTag(tag);
  }, []);

  const handleDelete = React.useCallback((tag: InventoryTagRecord) => {
    setDeletingTag(tag);
  }, []);

  const handleRowClick = React.useCallback(
    (cell: { row: InventoryTagRecord }) => {
      setLocation(`/inventory/tags/${cell.row.id}`);
    },
    [setLocation],
  );

  const handleConfirmDelete = React.useCallback(async () => {
    if (!deletingTag) return;
    try {
      await deleteTag({ id: deletingTag.id });
      toast.success(t.tagsListView.actions.deleteSuccess);
      refetch();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t.tagsListView.dialog.unknownError;
      toast.error(t.tagsListView.actions.deleteError(errorMessage));
    } finally {
      setDeletingTag(null);
    }
  }, [deletingTag, deleteTag, toast, t, refetch]);

  const columns = useInventoryTagsColumns({
    facilityMap,
    onRowClick: handleRowClick,
    onEdit: handleEdit,
    onDelete: handleDelete,
  });

  const facilityOptions = React.useMemo(
    () => [
      { key: 'all', label: t.tagsListView.filters.allFacilities },
      ...facilities.map((f) => ({ key: f.id, label: f.name })),
    ],
    [facilities, t],
  );

  const tagTypeOptions = React.useMemo(
    () => [
      { key: 'all', label: t.tagsListView.filters.allTypes },
      {
        key: enumTagType.WorkInProgress,
        label: t.tagsListView.tagTypes.WorkInProgress,
      },
      {
        key: enumTagType.FinishedGood,
        label: t.tagsListView.tagTypes.FinishedGood,
      },
    ],
    [t],
  );

  const statusOptions = React.useMemo(
    () => [
      { key: 'all', label: t.tagsListView.filters.allStatuses },
      {
        key: enumInventoryTagStatus.Active,
        label: t.tagsListView.statusTypes.Active,
      },
      {
        key: enumInventoryTagStatus.Draft,
        label: t.tagsListView.statusTypes.Draft,
      },
      {
        key: enumInventoryTagStatus.Confirmed,
        label: t.tagsListView.statusTypes.Confirmed,
      },
      {
        key: enumInventoryTagStatus.InInventory,
        label: t.tagsListView.statusTypes.InInventory,
      },
      {
        key: enumInventoryTagStatus.InTransfer,
        label: t.tagsListView.statusTypes.InTransfer,
      },
      {
        key: enumInventoryTagStatus.Loaded,
        label: t.tagsListView.statusTypes.Loaded,
      },
      {
        key: enumInventoryTagStatus.Reserved,
        label: t.tagsListView.statusTypes.Reserved,
      },
      {
        key: enumInventoryTagStatus.PendingPurchase,
        label: t.tagsListView.statusTypes.PendingPurchase,
      },
      {
        key: enumInventoryTagStatus.Shipped,
        label: t.tagsListView.statusTypes.Shipped,
      },
      {
        key: enumInventoryTagStatus.Sold,
        label: t.tagsListView.statusTypes.Sold,
      },
      {
        key: enumInventoryTagStatus.Transferred,
        label: t.tagsListView.statusTypes.Transferred,
      },
      {
        key: enumInventoryTagStatus.Revised,
        label: t.tagsListView.statusTypes.Revised,
      },
      {
        key: enumInventoryTagStatus.Cancelled,
        label: t.tagsListView.statusTypes.Cancelled,
      },
      {
        key: enumInventoryTagStatus.Voided,
        label: t.tagsListView.statusTypes.Voided,
      },
    ],
    [t],
  );

  const statsColumns = React.useMemo(
    (): ListStatsCardProps['stats'] => [
      {
        key: 'totalTags',
        label: t.tagsListView.summary.totalTags,
        value: stats.totalTags,
        icon: <LucideTag />,
        color: 'primary',
      },
      {
        key: 'activeTags',
        label: t.tagsListView.summary.activeTags,
        value: stats.activeTags,
        icon: <LucideCheckCircle />,
        color: 'success',
      },
      {
        key: 'draftTags',
        label: t.tagsListView.summary.draftTags,
        value: stats.draftTags,
        icon: <LucideFileText />,
        color: 'warning',
      },
      {
        key: 'inInventoryTags',
        label: t.tagsListView.summary.inInventoryTags,
        value: stats.inInventoryTags,
        icon: <LucidePackage />,
        color: 'primary',
      },
    ],
    [stats, t],
  );

  const emptyPanelProps = {
    title: t.tagsListView.emptyState.title,
    description: <span>{t.tagsListView.emptyState.subtitle}</span>,
    centered: true,
  };

  return (
    <div className="@InventoryTagsListView flex h-full flex-col">
      <ListScreenLayout
        filters={
          <>
            <SearchField
              autoFocus
              value={searchTerm ?? ''}
              onChange={(value) => {
                setSearchTerm(value || undefined);
                setOffset(0);
              }}
              placeholder={t.tagsListView.filters.search}
              className="w-64"
            />
            <Select
              value={selectedFacility || 'all'}
              onChange={(key) => {
                setSelectedFacility(key === 'all' ? undefined : String(key));
                setOffset(0);
              }}
              options={facilityOptions}
              className="w-48"
            />
            <Select
              value={selectedTagType || 'all'}
              onChange={(key) => {
                setSelectedTagType(key === 'all' ? undefined : String(key));
                setOffset(0);
              }}
              options={tagTypeOptions}
              className="w-40"
            />
            <Select
              value={selectedStatus || 'all'}
              onChange={(key) => {
                setSelectedStatus(key === 'all' ? undefined : String(key));
                setOffset(0);
              }}
              options={statusOptions}
              className="w-40"
            />
          </>
        }
        actions={
          <Button
            icon={<LucidePlus />}
            color="primary"
            onPress={handleCreateClick}
          >
            {t.tagsListView.createTag}
          </Button>
        }
        stats={statsColumns}
        footer={
          total > limit ? (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          ) : undefined
        }
      >
        <DataGrid<InventoryTagRecord>
          isLoading={loadingState.isInitialLoading || isFetching}
          columns={columns}
          dataSource={tags}
          rowKey="id"
          aria-label={t.tagsListView.table.aria}
          onColumnSort={handleColumnSort}
          className="w-full cursor-pointer text-sm"
          fullWidth
          hasError={!!error}
          errorPanelProps={
            error
              ? {
                  description: t.tagsListView.content.error(error.message),
                  centered: true,
                }
              : undefined
          }
          emptyPanelProps={emptyPanelProps}
        />
      </ListScreenLayout>

      <InventoryTagDialog
        isOpen={isCreateDialogOpen || !!editingTag}
        onClose={handleDialogClose}
        onSuccess={handleDialogSuccess}
        editingTag={editingTag}
      />

      {deletingTag && (
        <DialogOverlay isOpen className="@DeleteTagDialog">
          <DialogContent>
            <DialogHeader>
              <div className="flex items-center gap-2">
                <LucideAlertTriangle className="size-5 text-failure" />
                <span className="text-base font-semibold">
                  {t.tagsListView.confirmDelete.title}
                </span>
              </div>
            </DialogHeader>
            <div className="flex flex-col gap-2 px-4">
              <div className="text-base text-neutral/50">
                {t.tagsListView.confirmDelete.description(
                  deletingTag.tagNumber,
                )}
              </div>
              <div className="flex justify-end gap-2 py-4">
                <Button
                  variant="text"
                  color="neutral"
                  onPress={() => setDeletingTag(null)}
                >
                  {t.tagsListView.confirmDelete.cancel}
                </Button>
                <Button
                  variant="solid"
                  color="failure"
                  onPress={handleConfirmDelete}
                >
                  {t.tagsListView.confirmDelete.confirm}
                </Button>
              </div>
            </div>
          </DialogContent>
        </DialogOverlay>
      )}
    </div>
  );
};
